import React from 'react';
import { Shield, Sparkles, Users, Award, Globe, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { mockCompanyInfo } from '../mock';

export const About = () => {
  const values = [
    {
      icon: Shield,
      title: "Safety First",
      description: "We prioritize the protection of your people, assets, and brand reputation through rigorous security protocols."
    },
    {
      icon: Sparkles,
      title: "Excellence in Hygiene",
      description: "Our cleaning standards exceed industry norms, creating pristine environments that enhance your image."
    },
    {
      icon: Users,
      title: "Trained Professionals",
      description: "All staff undergo strict vetting and comprehensive training in accordance with South African regulations."
    },
    {
      icon: Globe,
      title: "Eco-Friendly Approach",
      description: "We use sustainable cleaning products and energy-efficient security solutions for environmental responsibility."
    }
  ];

  const stats = [
    { number: "500+", label: "Satisfied Clients" },
    { number: "4", label: "Provinces Served" },
    { number: "24/7", label: "Emergency Response" },
    { number: "10+", label: "Years Experience" }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About <span className="text-red-600">La-Klin</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {mockCompanyInfo.tagline}
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Story */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Our Story</h3>
            <div className="space-y-4 text-gray-600">
              <p>
                At La-Klin, we provide integrated security and cleaning solutions designed to create 
                safe, hygienic, and welcoming environments for businesses, residential estates, and 
                public institutions across South Africa.
              </p>
              <p>
                We understand that safety and cleanliness go hand in hand in protecting your people, 
                assets, and brand reputation. That's why we offer a one-stop solution combining reliable 
                guarding and security technology with professional cleaning and hygiene services.
              </p>
              <p>
                Our commitment to excellence, sustainability, and 24/7 reliability has made us a trusted 
                partner for organizations throughout Gauteng, Western Cape, KwaZulu-Natal, and Eastern Cape.
              </p>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Our Value to You</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-600 rounded-full mt-3 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Cost Efficiency</h4>
                  <p className="text-gray-600">One provider for both security and cleaning reduces overheads, improves accountability, and streamlines vendor management.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-600 rounded-full mt-3 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Peace of Mind</h4>
                  <p className="text-gray-600">Our highly trained staff, vetted through strict compliance with South African security and labour regulations.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-600 rounded-full mt-3 flex-shrink-0"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Tailored Solutions</h4>
                  <p className="text-gray-600">From corporate offices to residential complexes, we customize our services to meet your specific needs.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Core Values</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center p-6 hover:shadow-lg transition-all duration-300 border-2 hover:border-red-200">
                  <CardContent className="space-y-4 pt-6">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                      <IconComponent className="w-8 h-8 text-red-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">{value.title}</h4>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">Our Impact</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-red-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};